import { LitElement, html, css } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { marked } from 'marked';
import { sanitizeHtml } from '../../utils/sanitize';

@customElement('bia-text-renderer')
export class BiaTextRenderer extends LitElement {
  static styles = css`
    :host {
      display: block;
      min-width: 0;
      max-width: 100%;
    }
    :host p {
      margin: 0 0 0.5em;
      word-wrap: break-word;
      overflow-wrap: anywhere;
    }
    :host p:last-child { margin-bottom: 0; }
    :host a { color: var(--bia-primary); word-break: break-word; }
    :host ul, :host ol { margin: 0 0 0.5em; padding-left: 1.25em; }
    :host li { margin: 0.1em 0; word-wrap: break-word; overflow-wrap: anywhere; }
    :host pre {
      background: var(--bia-surface);
      padding: 0.5em;
      border-radius: 4px;
      overflow-x: auto;
      max-width: 100%;
      box-sizing: border-box;
    }
    :host code {
      background: var(--bia-surface);
      padding: 0.05em 0.3em;
      border-radius: 3px;
      font-size: 0.9em;
      word-break: break-word;
    }
    :host .bia-md {
      max-width: 100%;
      overflow-wrap: anywhere;
    }
    .table-wrap {
      overflow-x: auto;
      max-width: 100%;
    }
    :host table {
      border-collapse: collapse;
      margin: 0.35em 0 0.6em;
      font-size: 0.85em;
      width: 100%;
    }
    :host th, :host td {
      border: 1px solid var(--bia-border, #e0e0e0);
      padding: 4px 8px;
      text-align: left;
      vertical-align: top;
    }
    :host th {
      background: var(--bia-surface, #f5f5f5);
      font-weight: 600;
    }
    :host tr:nth-child(even) td {
      background: color-mix(in srgb, var(--bia-surface, #f5f5f5) 40%, transparent);
    }
  `;

  @property({ type: String }) content = '';

  @query('.bia-md') private container!: HTMLDivElement;

  updated(changed: Map<string, unknown>): void {
    if (changed.has('content') && this.container) {
      const raw = marked.parse(this.content, { async: false }) as string;
      const clean = sanitizeHtml(raw);
      // Build DOM via DOMParser (safer than innerHTML assignment; clean is already sanitized)
      const parsed = new DOMParser().parseFromString(`<div>${clean}</div>`, 'text/html');
      const source = parsed.body.firstElementChild;
      while (this.container.firstChild) {
        this.container.removeChild(this.container.firstChild);
      }
      if (source) {
        for (const node of Array.from(source.childNodes)) {
          this.container.appendChild(node);
        }
      }
    }
  }

  /** Returns all <table> elements rendered inside this component. */
  getTables(): HTMLTableElement[] {
    if (!this.container) return [];
    return Array.from(this.container.querySelectorAll('table'));
  }

  hasTables(): boolean {
    return this.getTables().length > 0;
  }

  render() {
    return html`<div class="bia-md"></div>`;
  }
}
